import { LumpsumPlanStatus } from '../plans-v4/lumpsum/api/common.types'
import { LumpsumPlansMockScenario, type LumpsumPlanGateMockConfig } from './data/lumpsum/lists'
import { SipPlanStatus } from '../plans-v4/sip/api/common.types'
import { SipPlansMockScenario, type SipMockYourPlanCounts, type SipPlanGateMockConfig } from './data/sip/overview'

/**
 * One knob for pre-plan vs rolled-out. Derives planStatus + daysToPlanDrop together
 * so they cannot disagree (e.g. ROLLED_OUT with a future countdown).
 *
 * STRATEGY              → QUESTIONNAIRE_NOT_INITIATED (Find your strategy; days unused)
 * STRATEGY_IN_PROGRESS  → QUESTIONNAIRE_IN_PROGRESS (Complete your strategy; days unused)
 * TIMER                 → QUESTIONNAIRE_SUBMITTED + daysToPlanDrop > 0
 *                         (QUESTIONNAIRE_RE_SUBMITTED shares the same UI path in app code)
 * DELAYED               → QUESTIONNAIRE_SUBMITTED + daysToPlanDrop = 0
 * ROLLED_OUT            → PLAN_ROLLED_OUT with sell/buy or SIP actions (days unused)
 * TOP_SHAPE             → PLAN_ROLLED_OUT with no actions (portfolio already matches the plan)
 */
export enum LumpsumPlanMockPhase {
  STRATEGY = 'STRATEGY',
  STRATEGY_IN_PROGRESS = 'STRATEGY_IN_PROGRESS',
  TIMER = 'TIMER',
  DELAYED = 'DELAYED',
  ROLLED_OUT = 'ROLLED_OUT',
  TOP_SHAPE = 'TOP_SHAPE'
}

type PlanMockTabInput = {
  phase: LumpsumPlanMockPhase
  isPlanSubscriptionEligible: boolean
  /** Only read when phase === TIMER. Forced to at least 1. */
  daysToPlanDrop?: number
}

export function resolveLumpsumPlanGateMock(input: PlanMockTabInput): LumpsumPlanGateMockConfig {
  const isPlanSubscriptionEligible = input.isPlanSubscriptionEligible

  if (input.phase === LumpsumPlanMockPhase.STRATEGY) {
    return {
      planStatus: LumpsumPlanStatus.QUESTIONNAIRE_NOT_INITIATED,
      isPlanSubscriptionEligible,
      daysToPlanDrop: 0,
      isTopShape: false
    }
  }

  if (input.phase === LumpsumPlanMockPhase.STRATEGY_IN_PROGRESS) {
    return {
      planStatus: LumpsumPlanStatus.QUESTIONNAIRE_IN_PROGRESS,
      isPlanSubscriptionEligible,
      daysToPlanDrop: 0,
      isTopShape: false
    }
  }

  if (input.phase === LumpsumPlanMockPhase.TIMER) {
    const daysToPlanDrop = input.daysToPlanDrop ?? 7
    return {
      planStatus: LumpsumPlanStatus.QUESTIONNAIRE_SUBMITTED,
      isPlanSubscriptionEligible,
      daysToPlanDrop: daysToPlanDrop > 0 ? daysToPlanDrop : 7,
      isTopShape: false
    }
  }

  if (input.phase === LumpsumPlanMockPhase.DELAYED) {
    return {
      planStatus: LumpsumPlanStatus.QUESTIONNAIRE_SUBMITTED,
      isPlanSubscriptionEligible,
      daysToPlanDrop: 0,
      isTopShape: false
    }
  }

  return {
    planStatus: LumpsumPlanStatus.PLAN_ROLLED_OUT,
    isPlanSubscriptionEligible,
    daysToPlanDrop: 0,
    isTopShape: input.phase === LumpsumPlanMockPhase.TOP_SHAPE
  }
}

export function resolveSipPlanGateMock(
  input: PlanMockTabInput & { yourPlanCounts: SipMockYourPlanCounts }
): SipPlanGateMockConfig {
  const isPlanSubscriptionEligible = input.isPlanSubscriptionEligible
  const yourPlanCounts = input.yourPlanCounts

  if (input.phase === LumpsumPlanMockPhase.STRATEGY) {
    return {
      planStatus: SipPlanStatus.QUESTIONNAIRE_NOT_INITIATED,
      isPlanSubscriptionEligible,
      daysToPlanDrop: 0,
      isTopShape: false,
      yourPlanCounts
    }
  }

  if (input.phase === LumpsumPlanMockPhase.STRATEGY_IN_PROGRESS) {
    return {
      planStatus: SipPlanStatus.QUESTIONNAIRE_IN_PROGRESS,
      isPlanSubscriptionEligible,
      daysToPlanDrop: 0,
      isTopShape: false,
      yourPlanCounts
    }
  }

  if (input.phase === LumpsumPlanMockPhase.TIMER) {
    const daysToPlanDrop = input.daysToPlanDrop ?? 7
    return {
      planStatus: SipPlanStatus.QUESTIONNAIRE_SUBMITTED,
      isPlanSubscriptionEligible,
      daysToPlanDrop: daysToPlanDrop > 0 ? daysToPlanDrop : 7,
      isTopShape: false,
      yourPlanCounts
    }
  }

  if (input.phase === LumpsumPlanMockPhase.DELAYED) {
    return {
      planStatus: SipPlanStatus.QUESTIONNAIRE_SUBMITTED,
      isPlanSubscriptionEligible,
      daysToPlanDrop: 0,
      isTopShape: false,
      yourPlanCounts
    }
  }

  return {
    planStatus: SipPlanStatus.PLAN_ROLLED_OUT,
    isPlanSubscriptionEligible,
    daysToPlanDrop: 0,
    isTopShape: input.phase === LumpsumPlanMockPhase.TOP_SHAPE,
    yourPlanCounts
  }
}

/**
 * Flip MSW responses for allocation mock handlers.
 *
 * Per tab: set `phase` (+ `daysToPlanDrop` only for TIMER). Never set planStatus/days by hand.
 * SIP Your Plan sizes live on `sip.yourPlanCounts`.
 */
export const ALLOCATION_PLANS_MOCK_CONFIG = {
  lumpsumListScenario: LumpsumPlansMockScenario.WITH_TAGS,
  sipListScenario: SipPlansMockScenario.NORMAL,

  rebalance: resolveLumpsumPlanGateMock({
    phase: LumpsumPlanMockPhase.ROLLED_OUT,
    isPlanSubscriptionEligible: true,
    daysToPlanDrop: 7
  }),
  allocation: resolveLumpsumPlanGateMock({
    phase: LumpsumPlanMockPhase.ROLLED_OUT,
    isPlanSubscriptionEligible: true,
    daysToPlanDrop: 7
  }),
  ideal: resolveLumpsumPlanGateMock({
    phase: LumpsumPlanMockPhase.ROLLED_OUT,
    isPlanSubscriptionEligible: true,
    daysToPlanDrop: 7
  }),
  sip: resolveSipPlanGateMock({
    phase: LumpsumPlanMockPhase.ROLLED_OUT,
    isPlanSubscriptionEligible: true,
    daysToPlanDrop: 7,
    // stop, start, increase, and decrease each add one header line when above 0. noAction does not.
    yourPlanCounts: {
      stop: 0,
      start: 1,
      increase: 0,
      decrease: 0,
      noAction: 0
    }
  })
}
